import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaFacebookF } from 'react-icons/fa';
import { FiCheck, FiExternalLink, FiRefreshCw, FiTrash2 } from 'react-icons/fi';
import {
  connectFacebookPages,
  disconnectFacebookPages,
  getFacebookConnectionErrorMessage,
  loadFacebookPageConnection,
  selectFacebookPage,
} from '../utils/FB/facebookPageConnection';
import { trackButton, trackEvent } from '../utils/analytics';
import './FacebookPageConnection.css';

const EMPTY_CONNECTION = {
  connected: false,
  selectedPageId: '',
  pages: [],
};

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

export default function FacebookPageConnection({ onSummaryChange }) {
  const [connection, setConnection] = useState(EMPTY_CONNECTION);
  const [selectedPageId, setSelectedPageId] = useState('');
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState('');
  const [notice, setNotice] = useState({ type: '', text: '' });

  const applyConnection = useCallback((nextConnection) => {
    const normalizedConnection = nextConnection || EMPTY_CONNECTION;
    setConnection(normalizedConnection);
    setSelectedPageId(normalizedConnection.selectedPageId || '');
  }, []);

  const loadConnection = useCallback(async () => {
    setLoading(true);
    setNotice({ type: '', text: '' });
    try {
      applyConnection(await loadFacebookPageConnection());
    } catch (error) {
      setNotice({
        type: 'error',
        text: getFacebookConnectionErrorMessage(error),
      });
    } finally {
      setLoading(false);
    }
  }, [applyConnection]);

  useEffect(() => {
    loadConnection();
  }, [loadConnection]);

  const selectedPage = useMemo(
    () => connection.pages?.find((page) => page.id === connection.selectedPageId) || null,
    [connection.pages, connection.selectedPageId],
  );
  useEffect(() => {
    onSummaryChange?.({
      loading,
      connected: connection.connected,
      pageName: selectedPage?.name || '',
      error: notice.type === 'error',
    });
  }, [loading, connection.connected, selectedPage?.name, notice.type, onSummaryChange]);

  const expiryDate = formatDate(connection.dataAccessExpiresAt || connection.tokenExpiresAt);
  const selectionChanged = Boolean(selectedPageId)
    && selectedPageId !== connection.selectedPageId;

  const handleConnect = async () => {
    setAction('connect');
    setNotice({ type: '', text: '' });
    trackButton('facebook_page_connect');

    try {
      const nextConnection = await connectFacebookPages();
      applyConnection(nextConnection);
      setNotice({
        type: 'success',
        text: nextConnection.connected
          ? 'Facebook Page access is connected.'
          : 'Facebook connection was not completed.',
      });
      trackEvent('facebook_page_connect_success', {
        pageCount: nextConnection.pages?.length || 0,
      });
    } catch (error) {
      const message = getFacebookConnectionErrorMessage(error);
      setNotice({ type: 'error', text: message });
      trackEvent('facebook_page_connect_error', { error: message.slice(0, 120) });
    } finally {
      setAction('');
    }
  };

  const handleSaveSelection = async () => {
    if (!selectedPageId || !selectionChanged) return;
    setAction('select');
    setNotice({ type: '', text: '' });
    trackButton('facebook_page_select', { pageId: selectedPageId });

    try {
      const nextConnection = await selectFacebookPage(selectedPageId);
      applyConnection(nextConnection);
      const nextPage = nextConnection.pages?.find((page) => page.selected);
      setNotice({
        type: 'success',
        text: `${nextPage?.name || 'Facebook Page'} will be used for future posts.`,
      });
    } catch (error) {
      setNotice({
        type: 'error',
        text: getFacebookConnectionErrorMessage(error),
      });
    } finally {
      setAction('');
    }
  };

  const handleDisconnect = async () => {
    const confirmed = window.confirm(
      'Disconnect Facebook from TingVision? Saved Page access will be removed. You can reconnect later.',
    );
    if (!confirmed) return;

    setAction('disconnect');
    setNotice({ type: '', text: '' });
    trackButton('facebook_page_disconnect');

    try {
      applyConnection(await disconnectFacebookPages());
      setNotice({ type: 'success', text: 'Facebook Page access was removed.' });
    } catch (error) {
      setNotice({
        type: 'error',
        text: getFacebookConnectionErrorMessage(error),
      });
    } finally {
      setAction('');
    }
  };

  return (
    <section className="facebookConnection" aria-labelledby="facebook-connection-title">
      <div className="facebookConnection__header">
        <div className="facebookConnection__identity">
          <span className="facebookConnection__icon" aria-hidden="true">
            <FaFacebookF />
          </span>
          <div>
            <div className="facebookConnection__titleRow">
              <h2 id="facebook-connection-title">Facebook Page</h2>
              {!loading && (
                <span className={`facebookConnection__status facebookConnection__status--${connection.connected ? 'connected' : 'disconnected'}`}>
                  {connection.connected ? <FiCheck /> : null}
                  {connection.connected ? 'Connected' : 'Not connected'}
                </span>
              )}
            </div>
            <p>
              Link the Page where you want TingVision to publish sightings. Nothing is posted automatically.
            </p>
          </div>
        </div>

        <div className="facebookConnection__actions">
          <button
            type="button"
            className="settingsButton"
            onClick={handleConnect}
            disabled={loading || Boolean(action)}
          >
            {connection.connected ? <FiRefreshCw /> : <FaFacebookF />}
            <span>
              {action === 'connect'
                ? 'Opening Facebook…'
                : (connection.connected ? 'Refresh access' : 'Connect Facebook')}
            </span>
          </button>
          {connection.connected ? (
            <button
              type="button"
              className="settingsButton settingsButton--ghost facebookConnection__disconnect"
              onClick={handleDisconnect}
              disabled={Boolean(action)}
            >
              <FiTrash2 />
              <span>{action === 'disconnect' ? 'Disconnecting…' : 'Disconnect'}</span>
            </button>
          ) : null}
        </div>
      </div>

      {notice.text ? (
        <div className={`facebookConnection__notice facebookConnection__notice--${notice.type}`} role="status">
          {notice.text}
        </div>
      ) : null}

      {loading ? (
        <div className="facebookConnection__empty">Checking your Facebook connection…</div>
      ) : connection.connected ? (
        <div className="facebookConnection__body">
          <div className="facebookConnection__summary">
            <div>
              <span>Connected Facebook account</span>
              <strong>{connection.facebookUserName || 'Facebook account'}</strong>
            </div>
            <div>
              <span>Page used for posting</span>
              <strong>{selectedPage?.name || 'Choose a Page below'}</strong>
            </div>
            {expiryDate ? (
              <div>
                <span>Reconnect by</span>
                <strong>{expiryDate}</strong>
              </div>
            ) : null}
          </div>

          <fieldset className="facebookConnection__pages">
            <legend>Choose the Page for future sighting posts</legend>
            {connection.pages?.map((page) => (
              <label
                key={page.id}
                className={`facebookConnection__page${selectedPageId === page.id ? ' facebookConnection__page--selected' : ''}${page.canPublish ? '' : ' facebookConnection__page--disabled'}`}
              >
                <input
                  type="radio"
                  name="facebook-page"
                  value={page.id}
                  checked={selectedPageId === page.id}
                  onChange={() => setSelectedPageId(page.id)}
                  disabled={!page.canPublish || Boolean(action)}
                />
                {page.pictureUrl ? (
                  <img src={page.pictureUrl} alt="" className="facebookConnection__avatar" />
                ) : (
                  <span className="facebookConnection__avatar facebookConnection__avatar--fallback" aria-hidden="true">
                    <FaFacebookF />
                  </span>
                )}
                <span className="facebookConnection__pageCopy">
                  <strong>{page.name}</strong>
                  <span>
                    {page.canPublish
                      ? (page.selected ? 'Current posting Page' : 'Available for posting')
                      : 'Your Facebook role cannot create Page content'}
                  </span>
                </span>
                {page.link ? (
                  <a
                    href={page.link}
                    target="_blank"
                    rel="noreferrer"
                    className="facebookConnection__pageLink"
                    onClick={(event) => event.stopPropagation()}
                    aria-label={`Open ${page.name} on Facebook`}
                  >
                    <FiExternalLink />
                  </a>
                ) : null}
              </label>
            ))}
          </fieldset>

          <div className="facebookConnection__footer">
            <p>
              TingVision stores Page access securely on the server. You will review each sighting before it is posted.
            </p>
            <button
              type="button"
              className="settingsButton"
              onClick={handleSaveSelection}
              disabled={!selectionChanged || Boolean(action)}
            >
              <FiCheck />
              <span>{action === 'select' ? 'Saving…' : 'Use selected Page'}</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="facebookConnection__empty">
          <p>
            Connect the Facebook account that manages your Page. Facebook will ask for permission to list Pages and prepare Page posting access.
          </p>
          <p className="facebookConnection__legal">
            You can disconnect at any time. See our <Link to="/privacy-policy">Privacy Policy</Link> and <Link to="/facebook-data-deletion">data deletion instructions</Link>.
          </p>
        </div>
      )}
    </section>
  );
}
